// The Studio's action panel (StudioPanel.tsx; pen "Studio · Action panel"),
// projected from the shell model: Chat (one of the open project's
// conversations, #404: the docked one when it is this project's, see
// studioConversation.ts; its head switches, starts, renames and deletes them,
// pen "Chat header"; the thread is bare: no start screen),
// Needs you (the export check's queue and what stands before export; an
// item opens its thing), and the one composer at the foot of both, bound
// to that conversation's draft: what it sends shows in Chat. There is
// always a conversation to type in (#431): when the docked one is not this
// project's, the chat is a fresh one, minted by the first keystroke. Info and
// Sources are pages in the middle (StudioAbout.tsx).

export { StudioRight };

import * as Core from "@bilda/core";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { MoveTextProposal } from "../../../app/ports/landing.js";
import type { ProjectIssues } from "../../../app/ports/project.js";
import { waitingCeilingHint } from "../../../components/Chat/WaitingGroup/WaitingGroup.js";
import { ConversationPicker } from "../../../components/ChatPanel/ConversationPicker.js";
import type { StudioCard } from "../../../components/Studio/studioCards.js";
import { openThingCommands } from "../../../components/Studio/studioDesk.js";
import { StudioComposer } from "../../../components/StudioPanel/StudioComposer.js";
import {
    type BeforeExport,
    needsYouCount,
    StudioNeedsYou,
} from "../../../components/StudioPanel/StudioNeedsYou.js";
import {
    type ActionView,
    StudioPanel,
} from "../../../components/StudioPanel/StudioPanel.js";
import { useNow } from "../../../hooks/useNow.js";
import { railListStatus } from "../../conversationSummary.js";
import { projectRows, studioConversationOf } from "../../studioConversation.js";
import { useAppShell } from "../context.js";
import { conversationRowsOf } from "../conversationRows.js";
import { ConversationThread } from "./ConversationCentre.js";

const StudioRight = ({
    view,
    onView,
    cards,
    issues,
    beforeExport,
    whole,
    proposals,
    moving,
    onShowMe,
    onMoveIt,
}: {
    readonly view: ActionView;
    readonly onView: (view: ActionView) => void;
    readonly cards: readonly StudioCard[];
    readonly issues: ProjectIssues | undefined;
    readonly beforeExport: BeforeExport | undefined;
    // What the composer works on when nothing is picked ("Whole course").
    readonly whole: string;
    // The assistant's proposals for how the course lands (how it lands in
    // the project's format), with the panel's Show me and Move it.
    readonly proposals: readonly MoveTextProposal[];
    readonly moving: string | undefined;
    readonly onShowMe: (p: MoveTextProposal) => void;
    readonly onMoveIt: (p: MoveTextProposal) => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const {
        dispatch,
        health,
        studio,
        openFilePicker,
        activeConversationId,
        sessionFor,
        sendChat,
        submitText,
        composerMentions,
        handleMentionsChange,
        mkConversationId,
        startNewConversation,
        library,
        frame,
        conversationSummaries,
        conversationList,
        retryConversationList,
        openConversation,
        submitConversationRename,
        setConfirmDeleteConversation,
    } = useAppShell();
    const nowMs = useNow(60_000);
    // The page the scope chip follows: one the author opened (the project's
    // own page is the whole already).
    const located = studio.location;
    const pageTitle =
        located === undefined || studio.desk.path.length === 0
            ? undefined
            : library?.materials.find((m) => m.id === located.pageId)?.title;
    const online = health === "online";
    const openThing = (id: string) =>
        studio.onDesk(...openThingCommands(cards, id));
    // The Studio shows a project, so `studioProject` is set; "" matches no
    // conversation (a fresh one shows) should it ever not be.
    const projectId = frame.studioProject ?? "";
    const listStatus = railListStatus(conversationList);
    const shown = studioConversationOf(
        projectId,
        activeConversationId,
        listStatus === "ready" ? conversationSummaries : null,
    );
    const conversationId = shown.type === "open" ? shown.conversationId : null;
    const untitled = t`New conversation`;
    const rows = conversationRowsOf(
        projectRows(conversationSummaries, projectId),
        conversationId,
        nowMs,
        i18n.locale,
        untitled,
    );
    const current = rows.find((row) => row.active);
    const session = sessionFor(conversationId);
    // PS-61: at the CL-21 ceiling the composer says so and Send is off.
    const atWaitingCeiling =
        session.conversation.filter(
            (e) => e.type === "author" && e.status?.type === "waiting",
        ).length >= Core.Limits.WAITING_MESSAGES_MAX;
    // The first keystroke in a fresh conversation mints it: it opens in the
    // panel holding what was typed, nascent until the first send.
    const editDraft = (text: string) => {
        const id = conversationId ?? mkConversationId();
        if (conversationId === null)
            dispatch({ type: "new-tab", conversationId: id });
        sendChat({ type: "edited_draft", conversationId: id, text });
    };

    return (
        <StudioPanel
            view={view}
            waiting={needsYouCount(issues, beforeExport) + proposals.length}
            onView={onView}
            bodies={{
                needs_you: (
                    <StudioNeedsYou
                        issues={issues}
                        beforeExport={beforeExport}
                        proposals={proposals}
                        moving={moving}
                        onShowMe={onShowMe}
                        onMoveIt={onMoveIt}
                        online={online}
                        sourced={cards.some((c) => c.type === "source")}
                        onOpen={openThing}
                        onAddSource={openFilePicker}
                    />
                ),
                chat: (
                    <>
                        <div className="bilda-studio-panel__chat-head">
                            <ConversationPicker
                                title={current?.title ?? untitled}
                                status={listStatus}
                                rows={rows}
                                onOpen={openConversation}
                                onNew={startNewConversation}
                                onRetry={retryConversationList}
                                {...(shown.type === "open" &&
                                shown.durable &&
                                current !== undefined
                                    ? {
                                          actions: {
                                              current:
                                                  current.title === untitled
                                                      ? ""
                                                      : current.title,
                                              maxLength:
                                                  Core.Limits.SHORT_TEXT_MAX,
                                              onRename: (title: string) =>
                                                  submitConversationRename(
                                                      current.id,
                                                      title,
                                                  ),
                                              onDelete: () =>
                                                  setConfirmDeleteConversation({
                                                      id: current.id,
                                                      title: current.title,
                                                  }),
                                          },
                                      }
                                    : {})}
                            />
                        </div>
                        {conversationId !== null && (
                            <ConversationThread
                                conversationId={conversationId}
                            />
                        )}
                    </>
                ),
            }}
            composer={
                <StudioComposer
                    draft={session.draft}
                    whole={whole}
                    targets={composerMentions.map((m) => m.title)}
                    {...(located === undefined || pageTitle === undefined
                        ? {}
                        : {
                              page: {
                                  title: pageTitle,
                                  whole: located.scope === "whole_project",
                                  onScopeToWhole: studio.scopeToWhole,
                                  onScopeToPage: studio.scopeToPage,
                              },
                          })}
                    onDraftChange={editDraft}
                    onSend={() => {
                        // A fresh conversation's draft is empty: nothing to send.
                        if (conversationId === null) return;
                        submitText(session.draft, conversationId);
                        onView("chat");
                    }}
                    onClearTargets={() => handleMentionsChange([])}
                    onAttach={openFilePicker}
                    limitHint={
                        atWaitingCeiling
                            ? t(
                                  waitingCeilingHint(
                                      Core.Limits.WAITING_MESSAGES_MAX,
                                  ),
                              )
                            : undefined
                    }
                />
            }
        />
    );
};
